import { planarSegmentsIntersect } from './planar-geometry.js';
import type { DataGeometry } from './data-layer.js';

const planar = (point: { lat: number; lng: number }) => ({ x: point.lng, y: point.lat });

function polygonSegmentsIntersect(a: { lat: number; lng: number }, b: { lat: number; lng: number }, c: { lat: number; lng: number }, d: { lat: number; lng: number }, epsilon: number) {
  return planarSegmentsIntersect(planar(a), planar(b), planar(c), planar(d), { epsilon, includeContact: false });
}

export function validPolygonPoints(points: Array<{ lat: number; lng: number }>, epsilon = 1e-10) {
  if (new Set(points.map((item) => `${item.lat},${item.lng}`)).size < 3) return false;
  let area = 0;
  for (let index = 0; index < points.length; index += 1) {
    const current = points[index];
    const next = points[(index + 1) % points.length];
    area += (current.lng - points[0].lng) * (next.lat - points[0].lat) - (next.lng - points[0].lng) * (current.lat - points[0].lat);
  }
  if (Math.abs(area) <= epsilon) return false;
  const edges = points.map((point, index) => {
    const next = points[(index + 1) % points.length];
    return { point, next, index, minX: Math.min(point.lng, next.lng), maxX: Math.max(point.lng, next.lng), minY: Math.min(point.lat, next.lat), maxY: Math.max(point.lat, next.lat) };
  });
  const tree = buildTree(edges);
  for (const edge of edges) {
    if (crossesTree(edge, tree, points.length, epsilon)) return false;
  }
  return true;
}

interface Edge {
  point: { lat: number; lng: number };
  next: { lat: number; lng: number };
  index: number;
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
}
interface Bounds {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
}
type EdgeTree = Bounds & ({ edges: Edge[] } | { children: [EdgeTree, EdgeTree] });
function buildTree(edges: Edge[]): EdgeTree {
  const bounds = { minX: Infinity, maxX: -Infinity, minY: Infinity, maxY: -Infinity };
  for (const edge of edges) {
    bounds.minX = Math.min(bounds.minX, edge.minX);
    bounds.maxX = Math.max(bounds.maxX, edge.maxX);
    bounds.minY = Math.min(bounds.minY, edge.minY);
    bounds.maxY = Math.max(bounds.maxY, edge.maxY);
  }
  if (edges.length <= 8) return { ...bounds, edges };
  const useX = bounds.maxX - bounds.minX >= bounds.maxY - bounds.minY;
  edges.sort((a, b) => useX ? a.minX + a.maxX - b.minX - b.maxX : a.minY + a.maxY - b.minY - b.maxY);
  const middle = Math.floor(edges.length / 2);
  return { ...bounds, children: [buildTree(edges.slice(0, middle)), buildTree(edges.slice(middle))] };
}
function crossesTree(edge: Edge, tree: EdgeTree, count: number, epsilon: number): boolean {
  if (edge.maxX < tree.minX || edge.minX > tree.maxX || edge.maxY < tree.minY || edge.minY > tree.maxY) return false;
  if ('children' in tree) return tree.children.some((child) => crossesTree(edge, child, count, epsilon));
  return tree.edges.some((other) => {
    if (other.index <= edge.index || other.index === (edge.index + 1) % count || (other.index + 1) % count === edge.index) return false;
    return polygonSegmentsIntersect(edge.point, edge.next, other.point, other.next, epsilon);
  });
}

export function validDataPolygonGeometry(geometry: DataGeometry): boolean {
  if (geometry.type === 'GeometryCollection') return geometry.geometries.every(validDataPolygonGeometry);
  if (geometry.type !== 'Polygon' && geometry.type !== 'MultiPolygon') return true;
  // SAFETY: Callers validate the coordinate structure with dataGeometrySchema first.
  const polygons = (geometry.type === 'Polygon' ? [geometry.coordinates] : geometry.coordinates) as number[][][][];
  return polygons.every((polygon) => polygon.every((ring) => validPolygonPoints(ring.slice(0, -1).map(([lng, lat]) => ({ lng, lat })), 0)));
}
